import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, FormControlLabel, IconButton, MenuItem, Radio, RadioGroup, TextField, Tooltip, Typography } from '@mui/material';

import { ADDRESS_TYPE_OPTIONS, resolveVoieCode } from '../../addressTypes';
import { SCORE_THRESHOLDS, scoreColor, tokens } from '../../theme-next';
import { FIELD_LENGTH_LIMITS } from './fieldLengthLimits';
import { isHourNotRead } from './hourNotRead';

export interface FieldEditEntry {
  fieldKey: string;
  label: string;
  value: string;
  /** Confiance 0-1 telle que renvoyée par l'OCR. */
  confidence: number;
  /** Documents qui ont fourni cette valeur (pour cliquer et switcher le viewer). */
  sources?: Array<{ docTypeLabel: string; docIndex: number }>;
  /**
   * Clé du champ "secondaire" embarqué dans le même contrôle (ex :
   * `heure_vente` paire avec `date_vente`). Quand renseigné, FieldEditRow
   * rend un input combiné (datetime-local) et propage les changements aux
   * DEUX overrides séparément.
   */
  pairedFieldKey?: string;
  pairedValue?: string;
  pairedConfidence?: number;
}

export interface FieldGroup {
  title: string;
  accentColor: string;
  fields: FieldEditEntry[];
}

interface FieldEditListProps {
  groups: FieldGroup[];
  overrides: Record<string, string>;
  onOverrideChange: (fieldKey: string, value: string) => void;
  onJumpToDoc?: (docIndex: number) => void;
  /** Champs validés visuellement par le relecteur (tick OK). */
  validatedFields?: Record<string, { validated_at?: string; validated_by?: number | null } | true>;
  /** Callback de toggle du tick OK pour un champ. */
  onToggleFieldValidation?: (fieldKey: string, validated: boolean) => void;
  /**
   * Affiche la colonne de score de confiance (défaut `true`). Mis à `false`
   * côté étape de vérification agence (`VerifyInfoScreen`), où l'on ne veut ni
   * le pourcentage ni la coloration « à vérifier » : seule la liaison
   * champ↔pièce nous intéresse.
   */
  showConfidence?: boolean;
  /**
   * Erreurs de validation bloquantes par `fieldKey` → message affiché sous le
   * champ + bordure rouge. Opt-in (VerifyInfoScreen), absent en review GOD →
   * aucun changement de rendu là-bas.
   */
  invalidFields?: Record<string, string>;
}

/**
 * Combine une date ISO ("2026-05-04") et une heure ("17:11") en valeur
 * datetime-local ("2026-05-04T17:11"). Tolérant aux deux moitiés vides.
 */
const composeDateTimeLocal = (date: string, time: string): string => {
  if (!date) return '';
  const hhmm = time && /^\d{2}:\d{2}/.test(time) ? time.slice(0, 5) : '00:00';
  return `${date}T${hhmm}`;
};

/** Inverse : "2026-05-04T17:11" → { date, time }. */
const splitDateTimeLocal = (value: string): { date: string; time: string } => {
  if (!value) return { date: '', time: '' };
  const [date, time] = value.split('T');
  return { date: date ?? '', time: time?.slice(0, 5) ?? '' };
};

/**
 * Détection des champs civilité : fieldKey === 'civilite' OU suffixe `_civilite`.
 * Couvre titulaire_civilite, cotitulaire_civilite, vendeur_civilite,
 * acheteur_civilite, civilite (PI / permis).
 */
const isCivilityFieldKey = (key: string): boolean => key === 'civilite' || key.endsWith('_civilite');

/**
 * Normalise une valeur de civilité OCR vers "M." / "Mme" (format des prompts
 * Anthropic et stockage downstream MiSIV/Pipedrive). Tolère les variantes
 * fréquentes "Mr", "Monsieur", "mr", "M" sans point, "Mlle"…
 */
const normalizeCivility = (raw: string): 'M.' | 'Mme' | '' => {
  if (!raw) return '';
  const upper = raw.trim().toUpperCase().replace(/\./g, '');
  if (upper === 'M' || upper === 'MR' || upper === 'MONSIEUR') return 'M.';
  if (upper === 'MME' || upper === 'MADAME' || upper === 'MLLE' || upper === 'MADEMOISELLE') return 'Mme';
  return '';
};

const toPct = (f: number): number => Math.round(f * 100);

/**
 * Champs qui doivent rendre un input HTML `type="date"` (vs un TextField texte
 * libre). Le format ISO `YYYY-MM-DD` est celui du natif HTML, et coïncide :
 *   - avec ce que produit l'OCR Anthropic (cf. ocrCgPrompts.ts)
 *   - avec ce qui est stocké en BDD (CG_Entities.DateOfBirth = VARCHAR(10) ISO)
 *   - avec ce que `moment(...).format('DD/MM/YYYY')` accepte au moment de
 *     l'envoi SOAP MISIV (`<DateNaissance>`, `<DateMEC>`, etc.)
 *
 * Donc pas de conversion à faire dans la stack : on garde ISO partout, MISIV
 * convertit à la volée à l'émission du SOAP.
 *
 * À étendre si d'autres champs date pure apparaissent (`date_emission`,
 * `date_expiration` permis/CNI, etc.).
 */
const DATE_ONLY_FIELD_KEYS = new Set<string>(['date_premiere_mec', 'date_naissance']);

/**
 * Champ « Type de voie » : rendu en <Select> alimenté par le registre canonique
 * (ADDRESS_TYPE_OPTIONS, addressTypes.ts). L'OCR remonte le libellé EXACTEMENT
 * imprimé sur la pièce ("CHE", "CHEMIN", "AV."…) : affiché brut dans un
 * TextField, l'agence voyait « che » alors que la modale SubmitCTVO affichait
 * bien « Chemin » (retour Biwiz 2026-07). `resolveVoieCode` absorbe toutes les
 * formes (code app, libellé long, abréviation AFNOR) et le Select affiche le
 * libellé ; la valeur émise est le code canonique — celui attendu en aval par
 * mapStoreToCreateCgPayload/normalizeTypeVoie (agence) et
 * normalizeTypeVoieToCode côté backend (review god), qui laissent tous deux
 * les codes inchangés.
 */
const isTypeVoieFieldKey = (key: string): boolean => key === 'type_voie' || key.endsWith('_type_voie');

/**
 * Liste de champs éditables, organisée par groupes (Véhicule / Vendeur /
 * Acheteur / Adresse / Co-titulaire). Chaque champ a un TextField toujours
 * éditable + pastille de confiance + pastilles cliquables des doc sources
 * pour faire pivoter le viewer à gauche.
 */
export const FieldEditList = ({
  groups,
  overrides,
  onOverrideChange,
  onJumpToDoc,
  validatedFields,
  onToggleFieldValidation,
  showConfidence = true,
  invalidFields,
}: FieldEditListProps) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {groups.map((group) =>
        group.fields.length === 0 ? null : (
          <FieldGroupCard
            key={group.title}
            group={group}
            overrides={overrides}
            onOverrideChange={onOverrideChange}
            onJumpToDoc={onJumpToDoc}
            validatedFields={validatedFields}
            onToggleFieldValidation={onToggleFieldValidation}
            showConfidence={showConfidence}
            invalidFields={invalidFields}
          />
        ),
      )}
    </Box>
  );
};

/** Pastille cliquable pointant un document du viewer (provenance ou référence). */
const DocChip = ({
  label,
  docIndex,
  onJumpToDoc,
}: {
  label: string;
  docIndex: number;
  onJumpToDoc?: (docIndex: number) => void;
}) => (
  <Box
    onClick={() => onJumpToDoc?.(docIndex)}
    sx={{
      fontSize: '10px',
      fontWeight: 600,
      padding: '1px 6px',
      borderRadius: `${tokens.radius.sm}px`,
      backgroundColor: tokens.color.bgMid,
      color: tokens.color.textSecondary,
      cursor: onJumpToDoc ? 'pointer' : 'default',
      border: `0.5px solid ${tokens.color.border}`,
      whiteSpace: 'nowrap',
      '&:hover': onJumpToDoc
        ? {
            backgroundColor: tokens.color.yellowLight,
            borderColor: tokens.color.yellow,
            color: tokens.color.dark,
          }
        : {},
    }}
  >
    {label}
  </Box>
);

const FieldGroupCard = ({
  group,
  overrides,
  onOverrideChange,
  onJumpToDoc,
  validatedFields,
  onToggleFieldValidation,
  showConfidence,
  invalidFields,
}: {
  group: FieldGroup;
  overrides: Record<string, string>;
  onOverrideChange: (fieldKey: string, value: string) => void;
  onJumpToDoc?: (docIndex: number) => void;
  validatedFields?: Record<string, { validated_at?: string; validated_by?: number | null } | true>;
  onToggleFieldValidation?: (fieldKey: string, validated: boolean) => void;
  showConfidence: boolean;
  invalidFields?: Record<string, string>;
}) => (
  <Box
    sx={{
      backgroundColor: tokens.color.white,
      border: `1px solid ${tokens.color.border}`,
      borderRadius: `${tokens.radius['2xl']}px`,
      boxShadow: tokens.shadow.card,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        padding: '14px 18px',
        borderBottom: `1px solid ${tokens.color.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: group.accentColor }} />
      <Typography
        sx={{
          fontSize: '15px',
          fontWeight: 800,
          color: tokens.color.dark,
          letterSpacing: '-0.01em',
        }}
      >
        {group.title}
      </Typography>
    </Box>
    {group.fields.map((field) => (
      <FieldEditRow
        key={field.fieldKey}
        field={field}
        currentValue={overrides[field.fieldKey] ?? field.value}
        pairedCurrentValue={field.pairedFieldKey ? (overrides[field.pairedFieldKey] ?? field.pairedValue ?? '') : ''}
        edited={
          (field.fieldKey in overrides && overrides[field.fieldKey] !== field.value) ||
          (Boolean(field.pairedFieldKey) &&
            field.pairedFieldKey! in overrides &&
            overrides[field.pairedFieldKey!] !== (field.pairedValue ?? ''))
        }
        onChange={(v) => onOverrideChange(field.fieldKey, v)}
        onPairedChange={field.pairedFieldKey ? (v) => onOverrideChange(field.pairedFieldKey!, v) : undefined}
        onJumpToDoc={onJumpToDoc}
        validated={Boolean(validatedFields && validatedFields[field.fieldKey])}
        onToggleValidation={onToggleFieldValidation ? (v) => onToggleFieldValidation(field.fieldKey, v) : undefined}
        showConfidence={showConfidence}
        errorMessage={invalidFields?.[field.fieldKey]}
      />
    ))}
  </Box>
);

const FieldEditRow = ({
  field,
  currentValue,
  pairedCurrentValue,
  edited,
  onChange,
  onPairedChange,
  onJumpToDoc,
  validated,
  onToggleValidation,
  showConfidence,
  errorMessage,
}: {
  field: FieldEditEntry;
  currentValue: string;
  pairedCurrentValue: string;
  edited: boolean;
  onChange: (v: string) => void;
  onPairedChange?: (v: string) => void;
  onJumpToDoc?: (docIndex: number) => void;
  validated: boolean;
  onToggleValidation?: (validated: boolean) => void;
  showConfidence: boolean;
  errorMessage?: string;
}) => {
  const conf = toPct(field.confidence);
  const color = scoreColor(conf);
  const hasError = Boolean(errorMessage);
  // Colonne confiance masquée (étape de vérification agence) → aucune
  // coloration « à vérifier » : on neutralise needsReview pour garder un rendu
  // sobre, seule la liaison champ↔pièce compte.
  const needsReview = showConfidence && conf < SCORE_THRESHOLDS.GREEN && conf > 0;
  // Retour Biwiz 2026-07 : les champs des groupes fixes sont conservés même quand
  // l'IA ne les a pas lus (cf. buildVerifyGroups `alwaysShow`). On les signale
  // discrètement — libellé teinté + liseré ambre + placeholder — pour que l'agence
  // sache quoi compléter, SANS dépendre de la colonne confiance (masquée côté
  // agence via showConfidence=false).
  const isUnrecognized = !currentValue && field.confidence === 0;
  // Colonnes de droite présentes selon le contexte : confiance (%) et/ou tick.
  const trailingColumns = [showConfidence ? 'auto' : null, onToggleValidation ? 'auto' : null]
    .filter(Boolean)
    .join(' ');
  const isDateTimePair = Boolean(field.pairedFieldKey) && onPairedChange !== undefined;
  const combinedValue = isDateTimePair ? composeDateTimeLocal(currentValue, pairedCurrentValue) : '';
  // Heure jamais lue sur les pièces (cf. `hourNotRead.ts` pour le détail) : on la
  // signale comme les champs non reconnus — liseré ambre + mention sous le champ —
  // sans dépendre de la colonne confiance, masquée côté agence.
  // `pairedConfidence` était jusqu'ici écrite par `buildVerifyGroups` et par
  // `CGReviewPage` mais lue par personne : c'est son premier consommateur.
  const hourNotRead = isHourNotRead({
    isDateTimePair,
    currentValue,
    pairedCurrentValue,
    pairedConfidence: field.pairedConfidence,
  });
  const isCivility = isCivilityFieldKey(field.fieldKey);
  const civilityValue = isCivility ? normalizeCivility(currentValue) : '';
  // Type de voie : valeur du Select = code canonique résolu depuis n'importe
  // quelle forme stockée ("che", "CHEMIN", "AV."…). Vide si non reconnu par
  // l'OCR (placeholder « Non reconnu — à compléter », cohérent TextField).
  const isTypeVoie = isTypeVoieFieldKey(field.fieldKey);
  const typeVoieValue = isTypeVoie && currentValue ? resolveVoieCode(currentValue) : '';
  // Champs date pure (sans heure) — alignés sur le rendu datetime-local du
  // pair date_vente+heure_vente mais avec type="date" et zéro paired field.
  // La valeur en/out est en ISO YYYY-MM-DD (HTML5 natif + cohérent BDD/OCR).
  const isDateOnly = DATE_ONLY_FIELD_KEYS.has(field.fieldKey);
  // Borne de longueur de la colonne cible (undefined = champ sans contrainte).
  const fieldLimit = FIELD_LENGTH_LIMITS[field.fieldKey];
  // Normalise la valeur OCR pour le natif HTML : tolère "1975-3-4" → "1975-03-04"
  // si jamais l'OCR a laissé du non-padded (peu probable mais safe).
  const dateOnlyValue = (() => {
    if (!isDateOnly || !currentValue) return '';
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(currentValue);
    if (!m) return currentValue; // laisse passer si format inattendu
    const [, y, mo, d] = m;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  })();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: `minmax(0, 200px) 1fr${trailingColumns ? ` ${trailingColumns}` : ''}`,
        },
        alignItems: 'center',
        gap: '14px',
        padding: '12px 18px',
        borderBottom: `0.5px solid ${tokens.color.border}`,
        backgroundColor: edited ? tokens.color.yellowLight : 'transparent',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: needsReview ? color : isUnrecognized ? tokens.color.yellowDark : tokens.color.textMuted,
            lineHeight: 1.3,
          }}
        >
          {field.label}
        </Typography>
        {edited && (
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              color: tokens.color.yellowDark,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              marginTop: '2px',
            }}
          >
            ✎ Modifié
          </Typography>
        )}
        {field.sources && field.sources.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
            {field.sources.map((src, idx) => (
              <DocChip
                key={`${src.docIndex}-${idx}`}
                label={src.docTypeLabel}
                docIndex={src.docIndex}
                onJumpToDoc={onJumpToDoc}
              />
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {isCivility ? (
          <Box
            sx={{
              padding: '4px 10px',
              borderRadius: '6px',
              border: `1px solid ${hasError ? tokens.color.red : needsReview ? color : tokens.color.border}`,
              backgroundColor: 'rgba(0,0,0,0.02)',
            }}
          >
            <RadioGroup row value={civilityValue} onChange={(e) => onChange(e.target.value)} sx={{ gap: '8px' }}>
              <FormControlLabel
                value="M."
                control={<Radio size="small" sx={{ padding: '4px' }} />}
                label={<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>M.</Typography>}
                sx={{ marginRight: 0 }}
              />
              <FormControlLabel
                value="Mme"
                control={<Radio size="small" sx={{ padding: '4px' }} />}
                label={<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Mme</Typography>}
                sx={{ marginRight: 0 }}
              />
            </RadioGroup>
          </Box>
        ) : isDateTimePair ? (
          <TextField
            type="datetime-local"
            value={combinedValue}
            onChange={(e) => {
              const { date, time } = splitDateTimeLocal(e.target.value);
              onChange(date);
              onPairedChange!(time);
            }}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: '2000-01-01T00:00', max: '3000-01-01T00:00' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: edited ? tokens.color.yellowLight : tokens.color.white,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError
                  ? tokens.color.red
                  : needsReview
                    ? color
                    : hourNotRead
                      ? tokens.color.yellow
                      : tokens.color.border,
                borderWidth: hasError || needsReview ? 1.5 : 1,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? tokens.color.red : needsReview ? color : tokens.color.borderDark,
              },
            }}
          />
        ) : isDateOnly ? (
          <TextField
            type="date"
            value={dateOnlyValue}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: '1900-01-01', max: '2100-12-31' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: edited ? tokens.color.yellowLight : tokens.color.white,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? tokens.color.red : needsReview ? color : tokens.color.border,
                borderWidth: hasError || needsReview ? 1.5 : 1,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? tokens.color.red : needsReview ? color : tokens.color.borderDark,
              },
            }}
          />
        ) : isTypeVoie ? (
          <TextField
            select
            value={typeVoieValue}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            fullWidth
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                const code = selected as string;
                if (!code) {
                  return (
                    <Typography component="span" sx={{ fontSize: '14px', color: tokens.color.textMuted }}>
                      Non reconnu — à compléter
                    </Typography>
                  );
                }
                return ADDRESS_TYPE_OPTIONS.find((opt) => opt.code === code)?.label ?? code;
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: edited ? tokens.color.yellowLight : tokens.color.white,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError
                  ? tokens.color.red
                  : needsReview
                    ? color
                    : isUnrecognized
                      ? tokens.color.yellow
                      : tokens.color.border,
                borderWidth: hasError || needsReview ? 1.5 : 1,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? tokens.color.red : needsReview ? color : tokens.color.borderDark,
              },
            }}
          >
            {ADDRESS_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            value={currentValue}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            fullWidth
            placeholder={isUnrecognized ? 'Non reconnu — à compléter' : undefined}
            // Borne de la colonne qui recevra la valeur : on empêche de SAISIR
            // plus long, plutôt que de laisser MySQL refuser l'écriture en fin
            // de soumission (cf. fieldLengthLimits). Une valeur déjà trop longue
            // venue de l'OCR reste affichée en entier — c'est la validation de
            // VerifyInfoScreen qui la signale, on ne tronque rien en douce.
            // Champs à mise en forme tolérée (SIRET et ses espaces) : pas de
            // maxLength, la longueur se mesure après normalisation.
            inputProps={
              fieldLimit && !fieldLimit.normalize
                ? { maxLength: Math.max(fieldLimit.max, currentValue.length) }
                : undefined
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: edited ? tokens.color.yellowLight : tokens.color.white,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError
                  ? tokens.color.red
                  : needsReview
                    ? color
                    : isUnrecognized
                      ? tokens.color.yellow
                      : tokens.color.border,
                borderWidth: hasError || needsReview ? 1.5 : 1,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? tokens.color.red : needsReview ? color : tokens.color.borderDark,
              },
            }}
          />
        )}
        {hasError && (
          <Typography
            sx={{
              marginTop: '6px',
              fontSize: '12px',
              fontWeight: 700,
              color: tokens.color.red,
              lineHeight: 1.35,
            }}
          >
            {errorMessage}
          </Typography>
        )}
        {!hasError && hourNotRead && (
          <Typography
            sx={{
              marginTop: '6px',
              fontSize: '12px',
              fontWeight: 700,
              color: tokens.color.dark,
              lineHeight: 1.35,
            }}
          >
            Heure non lue sur les pièces — 00:00 est un défaut, corrigez-la si la vente s’est faite à une autre
            heure.
          </Typography>
        )}
      </Box>
      {showConfidence && (
        <Box
          sx={{
            minWidth: 60,
            textAlign: 'right',
            fontSize: '13px',
            fontWeight: 800,
            color,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {conf > 0 ? `${conf}%` : '—'}
        </Box>
      )}
      {onToggleValidation && (
        <Tooltip
          title={
            validated
              ? 'Champ validé visuellement — décocher pour retirer'
              : 'Marquer ce champ comme vérifié visuellement'
          }
        >
          <IconButton
            size="small"
            onClick={() => onToggleValidation(!validated)}
            sx={{
              color: validated ? tokens.color.green : tokens.color.textMuted,
              '&:hover': { color: validated ? tokens.color.green : tokens.color.dark },
            }}
          >
            {validated ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
